import React from "react";

import { businessHealthScorePalette } from "../../../../Utils/Helper/businessHealth";
import {
  extractFlattenedFieldAPI,
  isValidField as isValidFieldAPI,
} from "../../../../Utils/WatchlistSummary/validation";

// ===== TYPES & INTERFACES =====

interface SectionData {
  sectionScore?: number;
  sectionHealthFlag?: string;
  sectionRelevanceFlag?: string;
}

interface TrafficSource {
  source?: string;
  pct?: number;
}

interface GeoDistribution {
  country?: string;
  pct?: number;
}

interface TrafficSectionProps {
  data: any;
  sectionData: any;
  accountName: string;
  organizationName?: string;
  onToggleAudio?: () => void;
}

// ===== SCORE BUBBLE COMPONENT =====

const TrafficScoreBubble: React.FC<{ score: number }> = ({ score }) => {
  const getScoreColors = (score: number) => businessHealthScorePalette(score);

  const colors = getScoreColors(score);

  return (
    <div style={{ position: "relative", width: 48, height: 48, flexShrink: 0 }}>
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: 48,
          height: 48,
          borderRadius: "50%",
          background: colors.outer,
          border: `2px solid ${colors.inner}`,
        }}
      />
      <div
        style={{
          position: "absolute",
          top: 6,
          left: 6,
          width: 36,
          height: 36,
          borderRadius: "50%",
          background: colors.inner,
          border: `1px solid ${colors.score}`,
        }}
      />
      <div
        style={{
          position: "absolute",
          top: -2,
          right: -2,
          width: 20,
          height: 20,
          borderRadius: "50%",
          background: colors.score,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: "2px solid white",
        }}
      >
        <span
          style={{
            color: "white",
            fontSize: 11,
            fontWeight: 700,
            lineHeight: 1,
          }}
        >
          {Math.round(score)}
        </span>
      </div>
    </div>
  );
};

// ===== MODAL HEADER COMPONENT =====

const TrafficModalHeader: React.FC<{
  accountName: string;
  sectionData: SectionData;
  onToggleAudio?: () => void;
}> = ({ accountName, sectionData, onToggleAudio }) => {
  const sectionScore = sectionData.sectionScore || 0;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "16px",
        marginBottom: "16px",
      }}
    >
      <TrafficScoreBubble score={sectionScore} />
      <div>
        <h3 style={{ margin: 0, fontSize: 18, color: "#8b5cf6" }}>
          <strong>{accountName}</strong> - <em>Digital Traffic Patterns</em>
        </h3>
      </div>
      <button
        id="traffic-audio-btn"
        title="Play traffic audio narration"
        onClick={onToggleAudio}
        style={{
          background: "none",
          border: "none",
          padding: 8,
          borderRadius: 6,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#8b5cf6",
        }}
      >
        <svg
          width={24}
          height={24}
          viewBox="0 0 24 24"
          fill="none"
          stroke="#8b5cf6"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M11 5 6 9H2v6h4l5 4V5Z" />
          <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
          <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
        </svg>
      </button>
    </div>
  );
};

// ===== MODAL BODY COMPONENT =====

const TrafficModalBody: React.FC<{
  synopsis?: string | null;
  confidenceScore?: number | null;
  dataFreshness?: string | null;
  lastUpdated?: string | null;
  sources?: any[];
  sectionData: SectionData;
  organizationName?: string;
}> = ({
  synopsis,
  confidenceScore,
  dataFreshness,
  sources,
  sectionData,
  organizationName,
}) => {
  const healthFlag = sectionData.sectionHealthFlag || "N/A";
  const relevanceFlag = sectionData.sectionRelevanceFlag || "N/A";
  const orgName = organizationName || "Organization";

  const sourcesText = Array.isArray(sources)
    ? sources
        .map(s => (typeof s === "object" ? s?.name : s))
        .filter(Boolean)
        .join(", ") || "Web traffic analytics"
    : "Web traffic analytics";

  const formatConfidenceScore = (score: number | null | undefined) => {
    if (score === null || score === undefined)
      return { display: "N/A", color: "#64748b" };
    if (typeof score === "number") {
      let percentValue;
      if (score <= 1) percentValue = Math.round(score * 100);
      else if (score <= 10) percentValue = Math.round(score * 10);
      else percentValue = Math.round(score);
      const display = `${percentValue}%`;
      let color = "#dc2626";
      if (percentValue >= 80) color = "#16a34a";
      else if (percentValue >= 60) color = "#d97706";
      return { display, color };
    }
    return { display: "N/A", color: "#64748b" };
  };

  const formatDataFreshness = (freshness: any) => {
    if (!freshness) return { display: "N/A", color: "#64748b" };
    const freshnessStr = String(freshness).toLowerCase();
    let color = "#64748b";
    if (freshnessStr.includes("recent") || freshnessStr.includes("up-to-date"))
      color = "#16a34a";
    else if (
      freshnessStr.includes("moderate") ||
      freshnessStr.includes("weekly")
    )
      color = "#d97706";
    else if (
      freshnessStr.includes("stale") ||
      freshnessStr.includes("outdated")
    )
      color = "#dc2626";
    return { display: freshness, color };
  };

  const confidenceData = formatConfidenceScore(confidenceScore);
  const freshnessData = formatDataFreshness(dataFreshness);

  const getHealthText = (flag: string) => {
    switch (flag) {
      case "G":
        return "GREEN";
      case "Y":
        return "YELLOW";
      case "R":
        return "RED";
      default:
        return "N/A";
    }
  };

  const getRelevanceText = (flag: string) => {
    switch (flag) {
      case "H":
        return "HIGH";
      case "M":
        return "MEDIUM";
      case "L":
        return "LOW";
      default:
        return "N/A";
    }
  };

  const badgeStyle = (bgColor: string) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "4px 12px",
    background: bgColor,
    borderRadius: 20,
    fontSize: 12,
    fontWeight: 500,
    color: "white",
  });

  return (
    <div
      style={{
        background: "white",
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        marginBottom: 16,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          gap: 12,
          padding: "12px 16px",
          background: "#f8fafc",
          borderBottom: "1px solid #e5e7eb",
        }}
      >
        <div
          style={badgeStyle(
            healthFlag === "G"
              ? "#16a34a"
              : healthFlag === "Y"
                ? "#d97706"
                : "#dc2626"
          )}
        >
          <span>Account Health:</span>
          <strong>{getHealthText(healthFlag)}</strong>
        </div>
        <div style={badgeStyle("#8b5cf6")}>
          <span>Relevance to {orgName}:</span>
          <strong>{getRelevanceText(relevanceFlag)}</strong>
        </div>
      </div>

      {synopsis ? (
        <div style={{ padding: 16 }}>
          <p
            style={{
              margin: "0 0 12px 0",
              lineHeight: 1.6,
              fontSize: 14,
              color: "#374151",
            }}
          >
            {synopsis}
          </p>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "20% 30% 50%",
              gap: 12,
              marginTop: 12,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 11,
                padding: "6px 8px",
                background: "#f8fafc",
                borderRadius: 4,
              }}
            >
              <span style={{ color: "#64748b", fontWeight: 500 }}>
                Confidence:
              </span>
              <span
                style={{
                  color: confidenceData.color,
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }}
              >
                {confidenceData.display}
              </span>
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                fontSize: 11,
                padding: "6px 8px",
                background: "#f8fafc",
                borderRadius: 4,
              }}
            >
              <span
                style={{ color: "#64748b", fontWeight: 500, marginBottom: 2 }}
              >
                Freshness:
              </span>
              <span
                style={{
                  color: freshnessData.color,
                  fontWeight: 600,
                  wordWrap: "break-word",
                  whiteSpace: "normal",
                  lineHeight: 1.3,
                }}
              >
                {freshnessData.display}
              </span>
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                fontSize: 11,
                padding: "6px 8px",
                background: "#f8fafc",
                borderRadius: 4,
              }}
            >
              <span
                style={{ color: "#64748b", fontWeight: 500, marginBottom: 2 }}
              >
                Sources:
              </span>
              <span
                style={{
                  color: "#1e293b",
                  fontWeight: 600,
                  wordWrap: "break-word",
                  whiteSpace: "normal",
                  lineHeight: 1.3,
                }}
              >
                {sourcesText}
              </span>
            </div>
          </div>
        </div>
      ) : (
        <div style={{ padding: 16 }}>
          <p
            style={{
              margin: 0,
              fontSize: 14,
              color: "#64748b",
              fontStyle: "italic",
            }}
          >
            No traffic analysis available.
          </p>
        </div>
      )}
    </div>
  );
};

// ===== ORGANIZATION RELEVANCE CARD =====

const OrganizationRelevanceCard: React.FC<{
  relevance?: string | null;
  organizationName?: string;
}> = ({ relevance, organizationName }) => {
  const orgName = organizationName || "Organization";
  if (!relevance) return null;

  return (
    <div
      style={{
        background: "white",
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        marginBottom: 16,
        overflow: "hidden",
      }}
    >
      <div style={{ padding: "16px 16px 8px 16px" }}>
        <h4
          style={{
            margin: "0 0 12px 0",
            fontSize: 16,
            fontWeight: 600,
            color: "#374151",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span>🎯</span> Relevance for {orgName}
        </h4>
      </div>
      <div style={{ padding: 16 }}>
        <p
          style={{ margin: 0, lineHeight: 1.6, fontSize: 14, color: "#374151" }}
        >
          {relevance}
        </p>
      </div>
    </div>
  );
};

// ===== TRAFFIC METRICS CARD =====

const TrafficMetricsCard: React.FC<{
  monthlyVisits?: number | null;
  monthlyChangePct?: number | null;
  globalRank?: number | null;
  timeOnSite?: number | null;
  pagesPerVisit?: number | null;
  bounceRate?: number | null;
}> = ({
  monthlyVisits,
  monthlyChangePct,
  globalRank,
  timeOnSite,
  pagesPerVisit,
  bounceRate,
}) => {
  const formatTrafficNumber = (num?: number | null): string => {
    if (!num || isNaN(num)) return "N/A";
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const hasMetrics =
    isValidFieldAPI(monthlyVisits) ||
    isValidFieldAPI(globalRank) ||
    isValidFieldAPI(timeOnSite) ||
    isValidFieldAPI(pagesPerVisit) ||
    isValidFieldAPI(bounceRate);

  if (!hasMetrics) return null;

  return (
    <div
      style={{
        background: "white",
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        marginBottom: 16,
        overflow: "hidden",
      }}
    >
      <div style={{ padding: "16px 16px 8px 16px" }}>
        <h4
          style={{
            margin: "0 0 12px 0",
            fontSize: 16,
            fontWeight: 600,
            color: "#374151",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span>📊</span> Key Traffic Metrics
        </h4>
      </div>
      <div style={{ padding: 16 }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
            gap: 12,
          }}
        >
          {isValidFieldAPI(monthlyVisits) && (
            <div
              style={{
                padding: 12,
                border: "1px solid #e5e7eb",
                borderRadius: 8,
              }}
            >
              <div
                style={{ fontWeight: 600, color: "#374151", marginBottom: 4 }}
              >
                Monthly Visits
              </div>
              <div style={{ fontSize: 18, fontWeight: 700, color: "#059669" }}>
                {formatTrafficNumber(monthlyVisits)}
              </div>
              {isValidFieldAPI(monthlyChangePct) && (
                <div
                  style={{
                    fontSize: 12,
                    color:
                      (monthlyChangePct ?? 0) > 0
                        ? "#16a34a"
                        : (monthlyChangePct ?? 0) < 0
                          ? "#dc2626"
                          : "#64748b",
                    marginTop: 4,
                  }}
                >
                  {(monthlyChangePct ?? 0) > 0 ? "+" : ""}
                  {(monthlyChangePct ?? 0).toFixed(1)}% MoM
                </div>
              )}
            </div>
          )}
          {isValidFieldAPI(globalRank) && (
            <div
              style={{
                padding: 12,
                border: "1px solid #e5e7eb",
                borderRadius: 8,
              }}
            >
              <div
                style={{ fontWeight: 600, color: "#374151", marginBottom: 4 }}
              >
                Global Rank
              </div>
              <div style={{ fontSize: 18, fontWeight: 700, color: "#059669" }}>
                #{(globalRank ?? 0).toLocaleString()}
              </div>
            </div>
          )}
          {isValidFieldAPI(timeOnSite) && (
            <div
              style={{
                padding: 12,
                border: "1px solid #e5e7eb",
                borderRadius: 8,
              }}
            >
              <div
                style={{ fontWeight: 600, color: "#374151", marginBottom: 4 }}
              >
                Avg. Session
              </div>
              <div style={{ fontSize: 18, fontWeight: 700, color: "#059669" }}>
                {(timeOnSite ?? 0).toFixed(1)} min
              </div>
            </div>
          )}
          {isValidFieldAPI(pagesPerVisit) && (
            <div
              style={{
                padding: 12,
                border: "1px solid #e5e7eb",
                borderRadius: 8,
              }}
            >
              <div
                style={{ fontWeight: 600, color: "#374151", marginBottom: 4 }}
              >
                Pages/Visit
              </div>
              <div style={{ fontSize: 18, fontWeight: 700, color: "#059669" }}>
                {(pagesPerVisit ?? 0).toFixed(1)}
              </div>
            </div>
          )}
          {isValidFieldAPI(bounceRate) && (
            <div
              style={{
                padding: 12,
                border: "1px solid #e5e7eb",
                borderRadius: 8,
              }}
            >
              <div
                style={{ fontWeight: 600, color: "#374151", marginBottom: 4 }}
              >
                Bounce Rate
              </div>
              <div style={{ fontSize: 18, fontWeight: 700, color: "#059669" }}>
                {(bounceRate ?? 0).toFixed(1)}%
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ===== TRAFFIC SOURCES CARD =====

const TrafficSourcesCard: React.FC<{ trafficSources?: TrafficSource[] }> = ({
  trafficSources,
}) => {
  if (!Array.isArray(trafficSources) || trafficSources.length === 0)
    return null;

  return (
    <div
      style={{
        background: "white",
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        marginBottom: 16,
        overflow: "hidden",
      }}
    >
      <div style={{ padding: "16px 16px 8px 16px" }}>
        <h4
          style={{
            margin: "0 0 12px 0",
            fontSize: 16,
            fontWeight: 600,
            color: "#374151",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span>📈</span> Traffic Sources
        </h4>
      </div>
      <div style={{ padding: 16 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {trafficSources.slice(0, 5).map((source, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "8px 12px",
                background: "#f8fafc",
                borderRadius: 6,
              }}
            >
              <span style={{ fontWeight: 500, color: "#374151" }}>
                {source.source || "Unknown"}
              </span>
              <span style={{ fontWeight: 600, color: "#059669" }}>
                {source.pct ? source.pct.toFixed(1) : "N/A"}%
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ===== GEOGRAPHIC DISTRIBUTION CARD =====

const GeographicDistributionCard: React.FC<{
  geoDistribution?: GeoDistribution[];
}> = ({ geoDistribution }) => {
  if (!Array.isArray(geoDistribution) || geoDistribution.length === 0)
    return null;

  return (
    <div
      style={{
        background: "white",
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        marginBottom: 16,
        overflow: "hidden",
      }}
    >
      <div style={{ padding: "16px 16px 8px 16px" }}>
        <h4
          style={{
            margin: "0 0 12px 0",
            fontSize: 16,
            fontWeight: 600,
            color: "#374151",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span>🌍</span> Geographic Distribution
        </h4>
      </div>
      <div style={{ padding: 16 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {geoDistribution.slice(0, 5).map((geo, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "8px 12px",
                background: "#f8fafc",
                borderRadius: 6,
              }}
            >
              <span style={{ fontWeight: 500, color: "#374151" }}>
                {geo.country || "Unknown Country"}
              </span>
              <span style={{ fontWeight: 600, color: "#059669" }}>
                {geo.pct ? geo.pct.toFixed(1) : "N/A"}%
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ===== ORIGINATION APPS CARD =====

const OriginationAppsCard: React.FC<{
  socialApps?: string[];
  paymentApps?: string[];
  analyticsApps?: string[];
  advertisingApps?: string[];
}> = ({ socialApps, paymentApps, analyticsApps, advertisingApps }) => {
  const hasApps =
    (Array.isArray(socialApps) && socialApps.length > 0) ||
    (Array.isArray(paymentApps) && paymentApps.length > 0) ||
    (Array.isArray(analyticsApps) && analyticsApps.length > 0) ||
    (Array.isArray(advertisingApps) && advertisingApps.length > 0);

  if (!hasApps) return null;

  const AppTags: React.FC<{ apps: string[]; color: string }> = ({
    apps,
    color,
  }) => (
    <>
      {apps.map((app, i) => (
        <span
          key={i}
          style={{
            display: "inline-flex",
            alignItems: "center",
            padding: "4px 8px",
            background: color,
            color: "white",
            borderRadius: 12,
            fontSize: 11,
            fontWeight: 500,
            margin: 2,
          }}
        >
          {app}
        </span>
      ))}
    </>
  );

  return (
    <div
      style={{
        background: "white",
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        marginBottom: 16,
        overflow: "hidden",
      }}
    >
      <div style={{ padding: "16px 16px 8px 16px" }}>
        <h4
          style={{
            margin: "0 0 12px 0",
            fontSize: 16,
            fontWeight: 600,
            color: "#374151",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span>🔧</span> Traffic Origination Apps
        </h4>
      </div>
      <div style={{ padding: 16 }}>
        {Array.isArray(socialApps) && socialApps.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <h5
              style={{
                margin: "0 0 8px 0",
                fontSize: 14,
                fontWeight: 600,
                color: "#374151",
              }}
            >
              Social Media
            </h5>
            <div>
              <AppTags apps={socialApps} color="#8b5cf6" />
            </div>
          </div>
        )}
        {Array.isArray(paymentApps) && paymentApps.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <h5
              style={{
                margin: "0 0 8px 0",
                fontSize: 14,
                fontWeight: 600,
                color: "#374151",
              }}
            >
              Payment & Finance
            </h5>
            <div>
              <AppTags apps={paymentApps} color="#059669" />
            </div>
          </div>
        )}
        {Array.isArray(analyticsApps) && analyticsApps.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <h5
              style={{
                margin: "0 0 8px 0",
                fontSize: 14,
                fontWeight: 600,
                color: "#374151",
              }}
            >
              Analytics & Tracking
            </h5>
            <div>
              <AppTags apps={analyticsApps} color="#0ea5e9" />
            </div>
          </div>
        )}
        {Array.isArray(advertisingApps) && advertisingApps.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <h5
              style={{
                margin: "0 0 8px 0",
                fontSize: 14,
                fontWeight: 600,
                color: "#374151",
              }}
            >
              Advertising & Marketing
            </h5>
            <div>
              <AppTags apps={advertisingApps} color="#f59e0b" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ===== SENTIMENT ANALYSIS CARD =====

const SentimentAnalysisCard: React.FC<{
  risks?: string | null;
  opportunities?: string | null;
  trafficTrend?: string | null;
}> = ({ risks, opportunities, trafficTrend }) => {
  const hasSentiment =
    isValidFieldAPI(risks) ||
    isValidFieldAPI(opportunities) ||
    isValidFieldAPI(trafficTrend);
  if (!hasSentiment) return null;

  return (
    <div
      style={{
        background: "white",
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        marginBottom: 16,
        overflow: "hidden",
      }}
    >
      <div style={{ padding: "16px 16px 8px 16px" }}>
        <h4
          style={{
            margin: "0 0 12px 0",
            fontSize: 16,
            fontWeight: 600,
            color: "#374151",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span>💭</span> Traffic Sentiment Analysis
        </h4>
      </div>
      <div style={{ padding: 16 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 12 }}>
          {isValidFieldAPI(risks) && (
            <div
              style={{
                padding: 12,
                border: "1px solid #fecaca",
                background: "#fef2f2",
                borderRadius: 8,
              }}
            >
              <h5
                style={{
                  margin: "0 0 8px 0",
                  fontSize: 14,
                  fontWeight: 600,
                  color: "#dc2626",
                }}
              >
                ⚠️ Risks
              </h5>
              <p
                style={{
                  margin: 0,
                  fontSize: 13,
                  lineHeight: 1.5,
                  color: "#374151",
                }}
              >
                {risks}
              </p>
            </div>
          )}
          {isValidFieldAPI(opportunities) && (
            <div
              style={{
                padding: 12,
                border: "1px solid #bbf7d0",
                background: "#f0fdf4",
                borderRadius: 8,
              }}
            >
              <h5
                style={{
                  margin: "0 0 8px 0",
                  fontSize: 14,
                  fontWeight: 600,
                  color: "#16a34a",
                }}
              >
                🚀 Opportunities
              </h5>
              <p
                style={{
                  margin: 0,
                  fontSize: 13,
                  lineHeight: 1.5,
                  color: "#374151",
                }}
              >
                {opportunities}
              </p>
            </div>
          )}
          {isValidFieldAPI(trafficTrend) && (
            <div
              style={{
                padding: 12,
                border: "1px solid #e5e7eb",
                background: "#f8fafc",
                borderRadius: 8,
              }}
            >
              <h5
                style={{
                  margin: "0 0 8px 0",
                  fontSize: 14,
                  fontWeight: 600,
                  color: "#374151",
                }}
              >
                📈 Traffic Trend
              </h5>
              <p
                style={{
                  margin: 0,
                  fontSize: 13,
                  lineHeight: 1.5,
                  color: "#374151",
                }}
              >
                {trafficTrend}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ===== MAIN TRAFFIC SECTION COMPONENT =====

export const TrafficSection: React.FC<TrafficSectionProps> = ({
  data,
  sectionData,
  accountName,
  organizationName,
  onToggleAudio,
}) => {
  if (!data) return <div>No traffic data available</div>;

  const synopsis = extractFlattenedFieldAPI(data, "synopsis", null);
  const confidenceScore = extractFlattenedFieldAPI(
    data,
    "metadata.confidence_score",
    null
  );
  const lastUpdated = extractFlattenedFieldAPI(
    data,
    "metadata.last_updated",
    null
  );
  const dataFreshness = extractFlattenedFieldAPI(
    data,
    "metadata.data_freshness",
    null
  );
  const sources = extractFlattenedFieldAPI(data, "metadata.sources", []);

  const monthlyVisits = extractFlattenedFieldAPI(
    data,
    "metrics.monthly_visits",
    null
  );
  const monthlyChangePct = extractFlattenedFieldAPI(
    data,
    "metrics.monthly_change_pct",
    null
  );
  const globalRankCurrent = extractFlattenedFieldAPI(
    data,
    "metrics.global_rank.current",
    null
  );
  const timeOnSiteMinutes = extractFlattenedFieldAPI(
    data,
    "metrics.time_on_site_minutes",
    null
  );
  const pagesPerVisit = extractFlattenedFieldAPI(
    data,
    "metrics.pages_per_visit",
    null
  );
  const bounceRatePct = extractFlattenedFieldAPI(
    data,
    "metrics.bounce_rate_pct",
    null
  );

  const trafficSources = extractFlattenedFieldAPI(
    data,
    "metrics.traffic_sources",
    []
  );
  const geographicDistribution = extractFlattenedFieldAPI(
    data,
    "metrics.geographic_distribution",
    []
  );

  const socialApps = extractFlattenedFieldAPI(
    data,
    "traffic_origination_apps.social",
    []
  );
  const paymentApps = extractFlattenedFieldAPI(
    data,
    "traffic_origination_apps.payment",
    []
  );
  const analyticsApps = extractFlattenedFieldAPI(
    data,
    "traffic_origination_apps.analytics",
    []
  );
  const advertisingApps = extractFlattenedFieldAPI(
    data,
    "traffic_origination_apps.advertising",
    []
  );

  const sentimentRisk = extractFlattenedFieldAPI(
    data,
    "sentiment_signal.risk",
    null
  );
  const sentimentOpportunity = extractFlattenedFieldAPI(
    data,
    "sentiment_signal.opportunity",
    null
  );
  const trafficTrend = extractFlattenedFieldAPI(
    data,
    "sentiment_signal.traffic_trend",
    null
  );

  const relevanceDescription = extractFlattenedFieldAPI(
    data,
    "organizationRelevance.description",
    null
  );

  return (
    <div className="traffic-section-consolidated">
      <TrafficModalHeader
        accountName={accountName}
        sectionData={sectionData}
        onToggleAudio={onToggleAudio}
      />
      <TrafficModalBody
        synopsis={synopsis}
        confidenceScore={confidenceScore}
        dataFreshness={dataFreshness}
        lastUpdated={lastUpdated}
        sources={sources}
        sectionData={sectionData}
        organizationName={organizationName}
      />
      <OrganizationRelevanceCard
        relevance={relevanceDescription}
        organizationName={organizationName}
      />
      <TrafficMetricsCard
        monthlyVisits={monthlyVisits}
        monthlyChangePct={monthlyChangePct}
        globalRank={globalRankCurrent}
        timeOnSite={timeOnSiteMinutes}
        pagesPerVisit={pagesPerVisit}
        bounceRate={bounceRatePct}
      />
      <TrafficSourcesCard trafficSources={trafficSources} />
      <GeographicDistributionCard geoDistribution={geographicDistribution} />
      <OriginationAppsCard
        socialApps={socialApps}
        paymentApps={paymentApps}
        analyticsApps={analyticsApps}
        advertisingApps={advertisingApps}
      />
      <SentimentAnalysisCard
        risks={sentimentRisk}
        opportunities={sentimentOpportunity}
        trafficTrend={trafficTrend}
      />
    </div>
  );
};
